// Note: If you add properties here which are not coming from the backend, please take a look at the
// fetchInitialState reducer in order not to overwrite properties.
// Note: It's not a static object, but function because initial state depends on what we have in
// session storage. When we use static object then in fetchInitialState we override the state wrongly
import { cadenzaSessionStorage } from '@disy/lib/storage';

import type { Data } from 'ui/data/data';

import { PARAMETER_MAXIMIZED_VIEW_ID } from 'cadenza/api-client/api-constants';
import type { RepositoryDto } from 'cadenza/api-client/dto/repository-dto';
import { parseWorkbookUrl } from 'cadenza/api-client/workbook-api';
import { createInitialOnDemandMapState } from 'cadenza/map/on-demand/on-demand-map.state';
import type { WorkbookConfigurationDto } from 'cadenza/model/workbook-configuration';
import type { WorkbookName } from 'cadenza/repository/model/workbook-name.gen';
import type { WorkbookNameAndHash } from 'cadenza/repository/model/workbook-name-and-hash';
import { STATUS } from 'cadenza/store/state-utils';
import { getUrlParam } from 'cadenza/utils/url-param-utils';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import { createInitialLiveModeState } from 'cadenza/workbook/state/workbook-live-mode-initial-state.state';
import { createInitialMapViewState } from 'cadenza/workbook/state/workbook-map-view.state';
import { createInitialPanelState } from 'cadenza/workbook/state/workbook-panel.state';
import { createInitialTextViewState } from 'cadenza/workbook/state/workbook-text-view.state';
import { WORKBOOK_CHANGED_KEY } from 'cadenza/workbook/workbook-core-constants.state';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

const parsedWorkbookUrl = parseWorkbookUrl(location.href)!;

export function createInitialWorkbookState(): WorkbookState {
  const workbookName = parsedWorkbookUrl?.workbookName ?? ('' as WorkbookName);
  return {
    workbookConfiguration: getWorkbookConfigFromPageData(),
    workbookName, // fallback for testing
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    workbookNameAndHash: parsedWorkbookUrl?.workbookNameAndHash ?? ({} as WorkbookNameAndHash), // fallback for testing
    activeWorksheetId: parsedWorkbookUrl?.worksheetId ?? ('' as WorksheetId), // fallback for testing
    maximizedViewId: getInitialMaximizedViewIdFromUrlParam(),
    detailViewId: '',
    printName: '',
    saved: false,
    changed: 'major',
    clientChanged: cadenzaSessionStorage.getItem(WORKBOOK_CHANGED_KEY) === workbookName,
    privileges: [],
    workbookType: 'WORKBOOK',
    settings: {},
    initializationStatus: STATUS.PENDING,
    commandStatus: STATUS.SUCCEEDED,
    commandErrorKey: '',
    projectById: {},
    dataSources: [],
    objecttypeById: {},
    reachableObjecttypesByBasicObjecttypeId: {},
    unavailableObjecttypeIds: [],
    objecttypeDataDependencies: { dependencyStateByObjecttypeId: {} },
    attributeById: {},
    activeAttributeById: {},
    worksheets: [],
    worksheetById: {},
    viewById: {},
    viewMetadataById: {},
    invalidViewsOrDataViews: [],
    invalidLayerIdsByViewId: {},
    dataViewsAffectedBySelectionChange: [],
    dataViewById: {},
    controllerValueByDataViewId: {},
    notificationsByView: {},
    reportById: {},
    embeddingTargetById: {},
    drillThroughTargets: [],
    conditionsAndGroups: [],
    conditionById: {},
    affectedViewsByConditionId: {},
    affectedViewsByHierarchyId: {},
    conditionGroupById: {},
    hierarchyById: {},
    dataViewToDataViewConnectionStatus: {},
    viewToDataViewConnectionStatus: {},
    reportMode: false,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    repository: {} as RepositoryDto, // for testing
    ...createInitialPanelState(),
    ...createInitialLiveModeState(),
    ...createInitialMapViewState(),
    ...createInitialTextViewState(),
    ...createInitialOnDemandMapState(),
  };
}

function getWorkbookConfigFromPageData() {
  return document.querySelector<Data<WorkbookConfigurationDto>>('#config-workbook')?.value;
}

function getInitialMaximizedViewIdFromUrlParam() {
  return (getUrlParam({ name: PARAMETER_MAXIMIZED_VIEW_ID }) as ViewId) || '';
}
